import { useState } from "react";
import { useNavigate } from "react-router";
import {
  FIND_SUGGEST_FROM_LENGTH,
  findObjects,
  recogniseCisNumber,
} from "@shared-core/domain/object-ui/find";
import {
  useFindConsignment,
  useFindRecords,
  useFindTharsternRecords,
} from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { paths } from "@shared-core/routing/routes";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";

/** Records suggested per object while typing; the results page shows more. */
const SUGGESTIONS_PER_OBJECT = 3;
/** Objects whose suggestions the box shows at once, the most looked-up first; the rest are on the results page. */
const SUGGESTED_OBJECTS = 5;

/**
 * Find (brief journey 2): a CIS number opens its record at once; from three characters, the records each list's own
 * search finds are suggested as the person types (FB-053). Enter with no record highlighted opens the results page,
 * never a list (FB-055). The number shapes come from the manifest, never restated here.
 */
export function FindBox() {
  const index = useObjectManifest();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const { objects, searched } = findObjects(index);
  const trimmed = text.trim();
  const match = recogniseCisNumber(text, objects);
  const consignments = useFindConsignment(text);
  const tharsternRecords = useFindTharsternRecords(text);
  // A CIS number names one record: no list is searched for it.
  const isSuggesting = !match && trimmed.length >= FIND_SUGGEST_FROM_LENGTH;
  const found = useFindRecords(isSuggesting ? trimmed : "", searched, {
    perObject: SUGGESTIONS_PER_OBJECT,
  });
  const groups = found.groups.slice(0, SUGGESTED_OBJECTS);

  const go = (href: string) => {
    setText("");
    setIsOpen(false);
    navigate(href);
  };

  return (
    <Command
      shouldFilter={false}
      label="Find"
      className="relative overflow-visible bg-transparent [&_[data-slot=command-input-wrapper]]:rounded-md [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:bg-input-background"
    >
      <CommandInput
        value={text}
        onValueChange={(next) => {
          setText(next);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setText("");
            setIsOpen(false);
          }
        }}
        placeholder="Find ORD-, QT-, PO-, INV-, a consignment number, a customer or supplier"
        aria-label="Find"
      />
      {isOpen && trimmed !== "" && (
        // On a phone the box is narrow: the options span the screen under the header.
        <CommandList className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] rounded-md border bg-popover shadow-md max-sm:fixed max-sm:inset-x-3 max-sm:top-[3.75rem] max-sm:mt-0">
          {/* The first option is what Enter does: a CIS number's record, else the results page. Lookups that answer
              later sit above it without taking Enter from it. */}
          {(match ||
            consignments.length > 0 ||
            tharsternRecords.length > 0) && (
            <CommandGroup heading="Open">
              {match && (
                <CommandItem
                  value={`open-${match.number}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => go(match.href)}
                >
                  Open {match.object.label} {match.number}
                </CommandItem>
              )}
              {consignments.map((consignment) => (
                <CommandItem
                  key={consignment.orderNo}
                  value={`open-consignment-${consignment.orderNo}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => go(consignment.href)}
                >
                  {consignment.label}
                  <span className="ml-2 text-muted-foreground">
                    {consignment.hint}
                  </span>
                </CommandItem>
              ))}
              {tharsternRecords.map((record) => (
                <CommandItem
                  key={record.object.id}
                  value={`open-${record.object.id}-${record.number}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => go(record.href)}
                >
                  Open {record.object.label} {record.number}
                  {record.title && (
                    <span className="ml-2 text-muted-foreground">
                      {record.title}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandGroup>
            <CommandItem
              value="see-all-results"
              onMouseDown={(event) => event.preventDefault()}
              onSelect={() => go(paths.find({ q: trimmed }))}
            >
              See all results for “{trimmed}”
            </CommandItem>
          </CommandGroup>
          {groups.map((group) => (
            <CommandGroup key={group.object.id} heading={group.object.plural}>
              {group.hits.map((hit) => (
                <CommandItem
                  key={`${hit.object}/${hit.key}`}
                  value={`record-${group.object.id}-${hit.object}-${hit.key}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => go(hit.href)}
                >
                  <span className="truncate">{hit.title}</span>
                  {hit.detail && (
                    <span className="ml-2 shrink-0 text-muted-foreground">
                      {hit.detail}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {isSuggesting && found.isSearching && groups.length === 0 && (
            <p
              role="status"
              className="px-3 py-2 text-sm text-muted-foreground"
            >
              Finding matches…
            </p>
          )}
          {isSuggesting &&
            !found.isSearching &&
            found.groups.length === 0 &&
            found.text === trimmed && (
              <p
                role="status"
                className="px-3 py-2 text-sm text-muted-foreground"
              >
                No records match “{trimmed}”.
              </p>
            )}
        </CommandList>
      )}
    </Command>
  );
}
